// src/pages/AddVocabularyToSTEP.jsx
import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowRight,
  Plus,
  Trash2,
  Save,
  Image as ImageIcon,
  X,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { stepQuestionsAPI } from "../../services/stepService";
import TranslationField from "../../components/TranslationField";

/* ------------------------------------------------------------------ */
/* Rich text editor for the question text (Bold / Italic / Underline / Highlight) */
/* ------------------------------------------------------------------ */
function ToolbarBtn({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "bg-blue-100 text-blue-700"
          : "text-gray-500 hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

function QuestionTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[90px] px-3 py-2 focus:outline-none",
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500">
      <div className="flex gap-1 border-b border-gray-200 p-1 bg-gray-50 rounded-t-lg">
        <ToolbarBtn
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Highlight"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function AddVocabularyToSTEP() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    question_text: "",
    explanation: "",
    english_explanation: "",
    translation: "",
    points: 1,
    difficulty: "MEDIUM",
    is_active: true,
  });
  const [options, setOptions] = useState(["", "", "", ""]);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleQuestionTextChange = (html) => {
    setForm((prev) => ({ ...prev, question_text: html }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleOptionChange = (idx, val) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? val : o)));
  };

  const addOption = () => {
    if (options.length < 4) setOptions((prev) => [...prev, ""]);
  };

  const removeOption = (idx) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((_, i) => i !== idx));
    if (correctIndex === idx) setCorrectIndex(null);
    else if (correctIndex > idx) setCorrectIndex((c) => c - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // question_text now comes from the rich text editor as HTML,
    // so an empty value looks like "<p></p>" instead of ""
    const isQuestionTextEmpty =
      !form.question_text || form.question_text.replace(/<[^>]*>/g, "").trim() === "";
    if (isQuestionTextEmpty) {
      setErrors({ question_text: "Question text is required" });
      return;
    }
    if (correctIndex === null) {
      setErrors({ correct_answer: "The correct answer must be selected" });
      return;
    }
    const filledOptions = options.filter((o) => o.trim());
    if (filledOptions.length < 2) {
      setErrors({ options: "At least two options must be added" });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const formData = new FormData();
      formData.append("question_text", form.question_text);
      formData.append("explanation", form.explanation);
      formData.append("english_explanation", form.english_explanation);
      formData.append("translation", form.translation || "");
      formData.append("points", form.points);
      formData.append("difficulty", form.difficulty);
      formData.append("is_active", form.is_active ? "True" : "False");
      formData.append("step_skill", skillId);
      formData.append("correct_answer", options[correctIndex]);
      formData.append("options", JSON.stringify(options.filter((o) => o.trim())));
      if (imageFile) {
        formData.append("question_image", imageFile);
      }

      await stepQuestionsAPI.createVocabulary(formData);
      navigate(`/dashboard/step/skills/${skillId}`);
    } catch (err) {
      setErrors(
        err.response?.data || { general: "An error occurred, please try again" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={`/dashboard/step/skills/${skillId}`}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        >
          <ArrowRight className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Add Vocabulary Question
          </h1>
          <p className="text-gray-500 text-sm">Multiple-choice question</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-5">
        {/* Question Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Text <span className="text-red-500">*</span>
          </label>
          <QuestionTextEditor
            value={form.question_text}
            onChange={handleQuestionTextChange}
          />
          {errors.question_text && (
            <p className="text-red-500 text-xs mt-1">{errors.question_text}</p>
          )}
          <TranslationField
            label="ترجمة نص السؤال"
            value={form.translation}
            onChange={(v) => setForm((prev) => ({ ...prev, translation: v }))}
          />
        </div>

        {/* Question Image */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Image (Optional)
          </label>
          {imagePreview ? (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full max-h-48 object-contain rounded-lg border border-gray-300"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-6 cursor-pointer hover:border-blue-400 transition-colors">
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Click to upload an image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Options */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Options <span className="text-red-500">*</span>{" "}
            <span className="text-xs text-gray-400">
              (Select the correct answer by clicking the circle)
            </span>
          </label>
          <div className="space-y-2">
            {options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCorrectIndex(idx)}
                  className={`w-6 h-6 rounded-full border-2 shrink-0 transition-colors ${
                    correctIndex === idx
                      ? "bg-green-500 border-green-500"
                      : "border-gray-300 hover:border-green-400"
                  }`}
                />
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => removeOption(idx)}
                  className="text-red-400 hover:text-red-600 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {options.length < 4 && (
            <button
              type="button"
              onClick={addOption}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add Option
            </button>
          )}
          {errors.options && (
            <p className="text-red-500 text-xs mt-1">{errors.options}</p>
          )}
          {errors.correct_answer && (
            <p className="text-red-500 text-xs mt-1">{errors.correct_answer}</p>
          )}
        </div>

        {/* Explanation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Explanation (Optional)
          </label>
          <textarea
            name="explanation"
            value={form.explanation}
            onChange={handleChange}
            rows={2}
            placeholder="Explain the correct answer..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
          />
        </div>
        {/* English Explanation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            English Explanation (Optional)
          </label>
          <textarea
            name="english_explanation"
            value={form.english_explanation}
            onChange={handleChange}
            rows={2}
            placeholder="Explain the correct answer in English..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none text-sm"
          />
        </div>
        {/* Points */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Points
          </label>
          <input
            type="number"
            name="points"
            value={form.points}
            onChange={handleChange}
            min={1}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Difficulty Level
          </label>
          <select
            name="difficulty"
            value={form.difficulty}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="EASY">Easy</option>
            <option value="MEDIUM">Medium</option>
            <option value="HARD">Hard</option>
          </select>
        </div>

        {errors.general && (
          <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">
            {errors.general}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg font-medium transition-colors"
        >
          <Save className="w-5 h-5" />
          {loading ? "Saving..." : "Save Question"}
        </button>
      </form>
    </div>
  );
}